import { useContext } from "react";

import { BaseCompanyLogo } from "../../../../components/BaseComponents/BaseCompanyLogo";
import BaseGridRatingHorizontal from "../../../../components/BaseComponents/BaseGridRatingHorizontal";
import BaseLinkWrapper from "../../../../components/BaseComponents/BaseLinkWrapper";
import { BaseVisitLink } from "../../../../components/BaseComponents/BaseVisitLink";
import { CompanyContext } from "../../../../components/CompanyList/CompanyContext";
import {
	useFoldPlacement,
	useFoldPlacementCompanies,
} from "../../../../components/FoldPlacements/useFoldPlacements";
import { BaseCompanies } from "../../../../components/UIComponents/BaseCompanies";
import { GiftBoxIcon } from "../../../../client/assets/icons/common/GiftBoxIcon";
import { RightArrowIcon } from "../../../../client/assets/icons/common/RightArrowIcon";

const INCORPORATION_SERVICES_FOLD_PLACEMENTS = {
	experimentKey: "prod-incorporation-services-fold-placements",
	placements: [
		{
			bannerText: "Privacy by Default",
			settingsKey: "fold_placement_highlight_left",
		},
		{
			bannerText: "Fastest Online Setup",
			settingsKey: "fold_placement_highlight_right",
		},
	],
	ctaText: "Start My LLC",
};

export const formatFoldPlacementPricingText = (pricing) => {
	const [, price, rest] =
		String(pricing)
			.trim()
			.match(/^(\d*\.?\d+)(.*)$/) ?? [];

	return price ? `$${price}${rest}` : null;
};

export const IncorporationServicesFoldPlacements = () => {
	const companies = useFoldPlacementCompanies(
		INCORPORATION_SERVICES_FOLD_PLACEMENTS,
	);

	if (companies.length === 0) {
		return null;
	}

	return (
		<div className="tw:flex tw:w-full tw:justify-center tw:gap-[8px] tw:pb-[16px] tw:xs:gap-[16px] tw:lg:gap-[30px] tw:lg:pb-[24px]">
			<BaseCompanies
				companies={companies}
				sliceStart={0}
				count={companies.length}
			>
				<IncorporationServicesFoldPlacementCard />
			</BaseCompanies>
		</div>
	);
};

const IncorporationServicesFoldPlacementCard = () => {
	const { company } = useContext(CompanyContext);
	const { bannerText, highlightText } = useFoldPlacement(
		INCORPORATION_SERVICES_FOLD_PLACEMENTS.placements,
	);
	const pricingText =
		highlightText ?? formatFoldPlacementPricingText(company.fields.pricing);

	return (
		<BaseLinkWrapper
			className="tw:group tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:rounded-[8px] tw:bg-white tw:shadow-[0px_1px_2px_0px_rgba(88,102,126,0.12),0px_4px_24px_0px_rgba(88,102,126,0.06)]"
			extraQueryParams={{ plc: "above_list_header" }}
		>
			<div className="tw:relative tw:flex tw:flex-col tw:lg:h-[32px] tw:lg:flex-row tw:lg:items-center tw:lg:justify-between tw:lg:rounded-t-[8px] tw:lg:bg-[#F9F5FE] tw:lg:pr-[20px]">
				<p className="tw:relative tw:z-1 tw:flex tw:h-[32px] tw:items-center tw:justify-center tw:rounded-t-[8px] tw:bg-[#4E1E86] tw:pb-[4px] tw:text-[12px] tw:leading-[16px] tw:font-semibold tw:text-white tw:[clip-path:polygon(0_0,100%_0,100%_calc(100%-4px),calc(50%+4px)_calc(100%-4px),50%_100%,calc(50%-4px)_calc(100%-4px),0_calc(100%-4px))] tw:xs:text-[14px] tw:lg:rounded-tr-none tw:lg:pr-[24px] tw:lg:pb-0 tw:lg:pl-[20px] tw:lg:leading-[24px] tw:lg:[clip-path:polygon(0_0,100%_0,calc(100%-12px)_52%,100%_100%,0_100%)]">
					{bannerText}
				</p>
				{pricingText && (
					<div className="tw:mt-[-4px] tw:flex tw:h-[28px] tw:items-center tw:justify-center tw:gap-[8px] tw:bg-[#F9F5FE] tw:pt-[4px] tw:lg:mt-0 tw:lg:h-auto tw:lg:bg-transparent tw:lg:pt-0">
						<p className="tw:text-[12px] tw:leading-[16px] tw:font-semibold tw:text-[#22293A] tw:xs:text-[14px] tw:lg:leading-[24px]">
							{pricingText}
						</p>
						<GiftBoxIcon className="tw:hidden tw:text-[22px] tw:text-[#22293A] tw:lg:inline-block" />
					</div>
				)}
			</div>

			<div className="tw:flex tw:flex-col tw:items-center tw:gap-[16px] tw:px-[8px] tw:py-[16px] tw:min-[360px]:px-[16px] tw:xs:p-[20px] tw:lg:flex-row tw:lg:justify-between">
				<div className="tw:flex tw:flex-col tw:items-center tw:gap-[8px] tw:lg:grid">
					<BaseCompanyLogo
						classNames={{
							picture:
								"tw:block tw:h-[28px] tw:w-full tw:max-w-[128px] tw:xs:h-[36px] tw:xs:max-w-none tw:xs:w-[164px] tw:lg:h-auto tw:lg:w-0 tw:lg:min-w-full",
							image: "tw:block tw:size-full tw:object-contain tw:lg:h-auto",
						}}
					/>

					<BaseGridRatingHorizontal
						classNames={{
							wrapper:
								"tw:flex tw:h-[24px] tw:items-center tw:gap-[3px] tw:rounded-full tw:bg-white tw:px-[6px] tw:shadow-[0px_5px_32px_0px_rgba(0,0,0,0.04),0px_10px_64px_0px_rgba(50,50,93,0.06)] tw:min-[360px]:gap-[4px] tw:min-[360px]:px-[8px] tw:xs:h-[28px] tw:xs:px-[10px] tw:lg:h-[32px] tw:lg:gap-[7px] tw:lg:px-[13px] tw:lg:shadow-[0px_4px_24px_0px_rgba(23,23,23,0.08),0px_0px_1px_0px_rgba(23,23,23,0.16)]",
							score:
								"tw:text-[11px] tw:leading-[16px] tw:font-bold tw:tracking-[-0.24px] tw:text-[#22293A] tw:min-[360px]:text-[12px] tw:xs:text-[16px] tw:lg:text-[18px] tw:lg:leading-[18px] tw:lg:tracking-[-0.36px]",
							stars: "tw:flex tw:items-center tw:gap-[4px]",
							starColor: "tw:text-[#359492]",
							starSize:
								"tw:text-[9px] tw:min-[360px]:gap-[1px] tw:min-[360px]:text-[10px] tw:xs:text-[14px] tw:lg:text-[16px]",
							count:
								"tw:text-[9px] tw:leading-[12px] tw:font-semibold tw:text-[#22293A] tw:min-[360px]:text-[10px] tw:xs:text-[12px] tw:lg:text-[14px] tw:lg:leading-[16px]",
						}}
					/>
				</div>

				<div className="tw:flex tw:h-[40px] tw:w-full tw:items-center tw:justify-center tw:rounded-[8px] tw:bg-[#359492] tw:transition-all tw:group-hover:bg-[#246161] tw:xs:h-[48px] tw:lg:h-[56px] tw:lg:w-[210px]">
					<BaseVisitLink
						wrapper="tw:flex tw:items-center tw:gap-[12px]"
						contentWrapper="tw:text-[14px] tw:leading-[24px] tw:font-semibold tw:text-white tw:xs:text-[16px] tw:lg:text-[18px] tw:lg:tracking-[-0.18px]"
						icon={<RightArrowIcon className="tw:text-[24px] tw:text-white" />}
						iconClass="tw:hidden tw:xs:flex"
					>
						{INCORPORATION_SERVICES_FOLD_PLACEMENTS.ctaText}
					</BaseVisitLink>
				</div>
			</div>
		</BaseLinkWrapper>
	);
};
